/**
 * Track Compare Store - Pinia
 *
 * Manages state for the track comparison feature:
 * - Selected tracks for comparison (configurable limit from settings)
 * - Compare session state (active, completed first run)
 * - Per-track color assignment (stable: hashed from track ID)
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import { useMapStore } from '@/stores/map';
import { useSettingsStore } from '@/stores/settings';

// 10 visually distinct colors for track overlays
export const COMPARE_PALETTE = [
  '#f97316', // orange
  '#3b82f6', // blue
  '#22c55e', // green
  '#a855f7', // purple
  '#ef4444', // red
  '#eab308', // yellow
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
  '#f59e0b'  // amber
];

/**
 * Returns a stable color for a track based on its ID.
 * @param {number} trackId
 * @returns {string} hex color
 */
export function getTrackCompareColor (trackId) {
  return COMPARE_PALETTE[Math.abs(trackId) % COMPARE_PALETTE.length];
}

export const useTrackCompareStore = defineStore('trackCompare', {
  state: () => ({
    /**
     * Selected tracks for comparison, in selection order
     * @type {Array<Object>}
     */
    selectedTracks: [],

    /**
     * Whether a compare session has been started (first "Start Compare" click)
     */
    compareActive: false,

    /**
     * Whether the compare results need refresh (tracks added after compareActive)
     */
    needsUpdate: false,

    /**
     * Whether stats computation is in progress
     */
    computing: false,

    /**
     * ID of the currently highlighted track (null = none)
     * @type {number|null}
     */
    activeTrackId: null
  }),

  getters: {
    selectedCount: (state) => state.selectedTracks.length,

    canCompare: (state) => state.selectedTracks.length >= 2,

    canSelectMore: (state) => {
      const settingsStore = useSettingsStore();
      return state.selectedTracks.length < (settingsStore.maxRoutesForComparison ?? 10);
    },

    selectedTrackIds: (state) => state.selectedTracks.map(t => t.id),

    isTrackSelected: (state) => (trackId) => {
      return state.selectedTracks.some(t => t.id === trackId);
    },

    /**
     * Per-track color map: { [trackId]: hexColor }
     */
    trackColors: (state) => {
      const map = {};
      for (const track of state.selectedTracks) {
        map[track.id] = getTrackCompareColor(track.id);
      }
      return map;
    }
  },

  actions: {
    toggleTrackSelection (track) {
      const settingsStore = useSettingsStore();
      const maxTracks = settingsStore.maxRoutesForComparison ?? 10;
      const existingIndex = this.selectedTracks.findIndex(t => t.id === track.id);

      if (existingIndex !== -1) {
        this.selectedTracks.splice(existingIndex, 1);
        if (this.compareActive) this.needsUpdate = true;
        return false;
      }

      if (this.selectedTracks.length < maxTracks) {
        this.selectedTracks.push(track);
        if (this.compareActive) this.needsUpdate = true;
        return true;
      }

      appDebugLog.warn('[TrackCompare] Maximum track limit reached:', maxTracks);
      return false;
    },

    deselectTrack (trackId) {
      const index = this.selectedTracks.findIndex(t => t.id === trackId);
      if (index !== -1) {
        this.selectedTracks.splice(index, 1);
        if (this.compareActive) this.needsUpdate = true;
      }
    },

    setActiveTrack (trackId) {
      this.activeTrackId = this.activeTrackId === trackId ? null : trackId;
      const mapStore = useMapStore();
      mapStore.setCompareActiveTrackId(this.activeTrackId);
    },

    clearSelection () {
      this.selectedTracks = [];
      this.compareActive = false;
      this.needsUpdate = false;
      this.activeTrackId = null;
    },

    /**
     * Start the comparison — displays tracks on map.
     * Sets compareActive and clears the needsUpdate flag.
     */
    startCompare () {
      if (!this.canCompare) {
        appDebugLog.warn('[TrackCompare] Need at least 2 tracks to compare');
        return false;
      }

      const mapStore = useMapStore();
      mapStore.displayMultipleTracks(this.selectedTracks, { colorMap: this._buildColorMap() });
      mapStore.setCompareActiveTrackId(null);

      this.compareActive = true;
      this.needsUpdate = false;
      return true;
    },

    /**
     * Refresh the comparison after tracks were added/removed post-start.
     */
    updateCompare () {
      if (!this.compareActive) return false;

      const mapStore = useMapStore();
      mapStore.displayMultipleTracks(this.selectedTracks, { colorMap: this._buildColorMap() });
      mapStore.setCompareActiveTrackId(null);

      this.needsUpdate = false;
      return true;
    },

    /**
     * Reset the compare session and clear map overlays.
     */
    resetCompareSession () {
      const mapStore = useMapStore();
      mapStore.displayMultipleTracks([]);
      mapStore.setCompareActiveTrackId(null);

      this.clearSelection();
    },

    /** Build a plain-object colorMap { [trackId]: hexColor } for the map store signal. */
    _buildColorMap () {
      const map = {};
      for (const track of this.selectedTracks) {
        map[track.id] = getTrackCompareColor(track.id);
      }
      return map;
    }
  }
});
